import { useState, useEffect } from 'react';
import { Loading, RATINGS, SpendChart, api } from './_barrel.js';


        // SVG stacked-bar chart for spend over time.
        // data is the response from /api/v1/admin/spend.
        // ============================================================
        // Overview tab — glanceable metrics dashboard.
        // Reads /subjects/{id}/metrics. Inline SVG charts (SpendChart
        // pattern), native <title> tooltips explaining each reading.
        // Hostile trend: DOWN is good (green), UP is bad (red).
        // ============================================================
        function OverviewTab({ subject, token, incidents, assessments, rubric, onGoToDetails }) {
            const [m, setM] = useState(null);
            const [err, setErr] = useState(null);
            // Change detection — capture the "last seen" timestamp for this subject
            // so we can flag what has arrived since the analyst last looked. We do
            // NOT stamp "now" on mount: doing so erased the banner on the first
            // tab-switch/refresh before the analyst could act on it. Instead the
            // timestamp is only advanced when the analyst explicitly dismisses the
            // banner (markSeen), so it persists until acknowledged. Purely local.
            const _seenKey = 'veil:seen:' + subject.id;
            const [lastSeen] = useState(() => { try { return localStorage.getItem(_seenKey); } catch (e) { return null; } });
            const [dismissed, setDismissed] = useState(false);
            const markSeen = () => {
                try { localStorage.setItem(_seenKey, new Date().toISOString()); } catch (e) { /* private mode */ }
                setDismissed(true);
            };
            useEffect(() => {
                let live = true;
                (async () => {
                    try {
                        const data = await api(`/api/v1/subjects/${subject.id}/metrics?weeks=12`, {}, token);
                        if (live) setM(data);
                    } catch (e) { if (live) setErr(e.message); }
                })();
                return () => { live = false; };
            }, [subject.id, token]);

            if (err) return <div className="panel p-6" style={{color:'var(--red)'}}>Metrics unavailable: {err}</div>;
            if (!m) return <div className="panel p-6 text-muted">Loading metrics…</div>;

            const delta = m.hostile_delta;
            const deltaGood = delta <= 0;               // fewer hostile = good
            const deltaColour = deltaGood ? 'var(--green)' : 'var(--red)';
            const deltaArrow = delta === 0 ? '·' : (delta < 0 ? '↓' : '↑');
            const disp = m.dispersion;
            const dispReading = disp == null ? 'n/a'
                : (disp >= 0.6 ? 'dispersed' : (disp < 0.3 ? 'concentrated' : 'mixed'));
            const dispColour = disp == null ? 'var(--muted)'
                : (disp >= 0.6 ? 'var(--green)' : (disp < 0.3 ? 'var(--red)' : 'var(--amber)'));

            const KPI = ({label, value, sub, subColour, tip}) => (
                <div className="panel p-4" title={tip} style={{cursor:'help'}}>
                    <div className="font-mono text-xs uppercase tracking-widest text-muted">{label}</div>
                    <div className="font-display" style={{fontSize:'32px', lineHeight:1.1, marginTop:'6px'}}>{value}</div>
                    {sub && <div className="font-mono text-xs mt-1" style={{color:subColour||'var(--muted)'}}>{sub}</div>}
                </div>
            );

            // ---- Timeline (hostile area + total line) ----
            const wk = m.weekly;
            const W=760, H=200, pL=44, pR=16, pT=16, pB=28;
            const plotW=W-pL-pR, plotH=H-pT-pB;
            const maxV = Math.max(1, ...wk.map(w => w.total));
            const n = wk.length;
            const xAt = i => pL + (n<=1 ? plotW/2 : (i/(n-1))*plotW);
            const yAt = v => pT + plotH*(1 - v/maxV);
            const hostilePts = wk.map((w,i)=>`${xAt(i)},${yAt(w.hostile)}`).join(' ');
            const totalPts = wk.map((w,i)=>`${xAt(i)},${yAt(w.total)}`).join(' ');
            const areaPts = `${pL},${pT+plotH} ${hostilePts} ${xAt(n-1)},${pT+plotH}`;

            // ---- Severity bars ----
            const sev = m.severity_mix;
            const sevMax = Math.max(1, sev.critical, sev.high, sev.moderate, sev.low);
            const sevRow = (label, val, colour) => (
                <div style={{display:'flex',alignItems:'center',gap:'12px',margin:'8px 0'}}
                     title={`${val} ${label.toLowerCase()}-severity hostile incident(s) in the last 30 days`}>
                    <span className="font-mono text-xs" style={{width:'70px',color:'var(--muted)'}}>{label}</span>
                    <div style={{flex:1,height:'9px',background:'var(--zone-3)'}}>
                        <div style={{width:`${(val/sevMax)*100}%`,height:'100%',background:colour}}></div>
                    </div>
                    <span className="font-mono text-xs" style={{width:'24px',textAlign:'right'}}>{val}</span>
                </div>
            );

            // ---- Intelligence at a glance (headline picture above the trends) ----
            const _active = assessments && assessments.items ? assessments.items.find(a => !a.superseded_at) : null;
            const _allInc = (incidents && incidents.items) ? incidents.items : [];
            const _openInc = _allInc.filter(i => !['closed','false_positive'].includes(i.status));
            const _sanctions = _openInc.filter(i => i.incident_type_code === 'sanctions_match' || (i.incident_type_code||'').includes('sanction'));
            const _breach = _openInc.filter(i => ['breach','breach_exposure','credential_breach','data_breach'].includes(i.incident_type_code));
            const _isHostile = (i) => (i.llm_classification && i.llm_classification.startsWith('hostile_')) || ['threat','doxxing','impersonation','disinformation'].includes(i.incident_type_code);
            const _hostileOpen = _openInc.filter(_isHostile).length;
            // Items detected since the analyst last viewed this subject.
            const _newSince = lastSeen ? _allInc.filter(i => i.detected_at && new Date(i.detected_at) > new Date(lastSeen)) : [];
            const _newHostile = _newSince.filter(_isHostile).length;
            const bandKey = _active ? String(_active.risk_band).toLowerCase() : null;
            // Score trend vs the previous signed assessment (change over time).
            const _history = (assessments && assessments.items ? assessments.items.slice() : [])
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            const _prev = _history.length > 1 ? _history[1] : null;
            let _trend = null;
            if (_active && _prev && _active.composite_score != null && _prev.composite_score != null) {
                const d = Number(_active.composite_score) - Number(_prev.composite_score);
                _trend = { d, arrow: d > 0.05 ? '↑' : (d < -0.05 ? '↓' : '→'),
                           colour: d > 0.05 ? 'var(--red)' : (d < -0.05 ? 'var(--green)' : 'var(--muted)') };
            }
            const Glance = ({label, value, colour, sub, tip}) => (
                <div className="panel p-4" title={tip} style={{cursor: tip ? 'help' : 'default'}}>
                    <div className="font-mono text-xs uppercase tracking-widest text-muted">{label}</div>
                    <div className="font-display" style={{fontSize:'26px', lineHeight:1.15, marginTop:'6px', color: colour || 'var(--paper)'}}>{value}</div>
                    {sub && <div className="font-mono text-xs mt-1 text-muted">{sub}</div>}
                </div>
            );

            return (
                <div className="space-y-5">
                    {/* Change detection — what's arrived since the last visit. Persists
                        until the analyst dismisses it (does NOT auto-clear on view). */}
                    {_newSince.length > 0 && !dismissed && (
                        <div className="p-3" style={{background:'var(--amber-bg)', border:'1px solid var(--amber)', borderRadius:'var(--radius-sm)', display:'flex', alignItems:'center', gap:'10px'}}>
                            <span style={{background:'var(--amber)', color:'var(--bg)', fontSize:'9px', fontWeight:700, letterSpacing:'0.1em', padding:'2px 6px', borderRadius:'3px'}}>NEW</span>
                            <span className="text-sm" style={{color:'var(--text-soft)', flex:1}}>
                                <strong>{_newSince.length}</strong> new item{_newSince.length===1?'':'s'}{_newHostile>0 && <span style={{color:'var(--red)'}}> ({_newHostile} hostile)</span>} since your last visit
                                {lastSeen && <span className="text-muted"> on {new Date(lastSeen).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</span>}.
                            </span>
                            {typeof onGoToDetails === 'function' && (
                                <button onClick={onGoToDetails}
                                        className="font-mono"
                                        style={{fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--gold)', background:'none', border:'1px solid var(--gold)', borderRadius:'4px', padding:'4px 9px', cursor:'pointer', flexShrink:0}}>
                                    Review in Details →
                                </button>
                            )}
                            <button onClick={markSeen} title="Mark as seen — dismiss this notice"
                                    style={{fontSize:'14px', lineHeight:1, color:'var(--text-muted)', background:'none', border:'none', cursor:'pointer', flexShrink:0, padding:'2px 4px'}}>
                                ✕
                            </button>
                        </div>
                    )}

                    {/* Intelligence at a glance */}
                    <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'14px'}}>
                        <Glance label="Risk Band"
                                value={bandKey ? RATINGS.label(bandKey) : 'Not set'}
                                colour={bandKey ? RATINGS.hex(bandKey) : 'var(--muted)'}
                                sub={_active
                                    ? <span>Composite {Number(_active.composite_score).toFixed(1)}{_trend && <span style={{color:_trend.colour, marginLeft:'6px'}}>{_trend.arrow} {_trend.d>0?'+':''}{_trend.d.toFixed(1)} vs last</span>}</span>
                                    : 'No assessment on file'}
                                tip="Current signed risk band and composite (0-5). The arrow compares it to the previous signed assessment. Same bands as the report and the dashboard." />
                        <Glance label="Open Incidents"
                                value={_openInc.length}
                                colour={_openInc.length ? 'var(--paper)' : 'var(--green)'}
                                sub={`${_hostileOpen} hostile · ${_allInc.length} all-time`}
                                tip="Incidents still open (not closed or dismissed). Hostile count in the sub-line." />
                        <Glance label="Breach Exposure"
                                value={_breach.length ? _breach.length : 'Clear'}
                                colour={_breach.length ? 'var(--amber)' : 'var(--green)'}
                                sub={_breach.length ? 'active breach incident(s)' : 'none on file'}
                                tip="Active breach/credential-exposure incidents for this principal (Have I Been Pwned)." />
                        <Glance label="Sanctions"
                                value={_sanctions.length ? 'FLAGGED' : 'Clear'}
                                colour={_sanctions.length ? 'var(--red)' : 'var(--green)'}
                                sub={_sanctions.length ? `${_sanctions.length} match(es)` : 'no match'}
                                tip="Open sanctions-screening matches. Any hit is a categorical escalation." />
                    </div>

                    {/* Coverage header */}
                    <div className="font-mono text-xs text-muted" style={{letterSpacing:'0.04em'}}>
                        {m.weeks_monitored} week{m.weeks_monitored===1?'':'s'} monitored
                        {m.coverage_start && ` · since ${new Date(m.coverage_start).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}`}
                    </div>

                    {/* KPI strip */}
                    <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'14px'}}>
                        <KPI label="Hostile · 30d" value={m.hostile_30d}
                             sub={`${deltaArrow} ${delta>0?'+':''}${delta} vs prev 30d`} subColour={deltaColour}
                             tip="Hostile incidents detected in the last 30 days, versus the 30 days before. For a protectee, a fall (green) is good news — the threat picture is cooling." />
                        <KPI label="Threat actors" value={m.distinct_hostile_accounts}
                             sub="distinct accounts" subColour="var(--muted)"
                             tip="Number of distinct accounts responsible for hostile content against this principal, all-time. The same figure drives the Watched Accounts panel and the report." />
                        <KPI label="Dispersion" value={disp==null?'—':disp.toFixed(2)}
                             sub={dispReading} subColour={dispColour}
                             tip="Distinct hostile accounts divided by hostile incidents. Above 0.60 reads as dispersed (many independent people, not organised). Below 0.30 suggests possible coordination worth monitoring." />
                        <KPI label="Total hostile" value={m.total_hostile}
                             sub="all-time" subColour="var(--muted)"
                             tip="All hostile-classified, non-closed incidents on file for this principal since monitoring began." />
                    </div>

                    {/* Timeline */}
                    <div className="panel p-6">
                        <div className="font-mono text-xs uppercase tracking-widest text-muted mb-3">Hostile Activity · By Week (detection time)</div>
                        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{display:'block'}}>
                            {[0,0.5,1].map((f,i)=>(
                                <line key={i} x1={pL} y1={pT+plotH*f} x2={W-pR} y2={pT+plotH*f} stroke="#232d42" strokeDasharray="2,3"/>
                            ))}
                            <polyline points={areaPts} fill="rgba(168,50,50,0.10)" stroke="none"/>
                            <polyline points={totalPts} fill="none" stroke="#79839a" strokeWidth="1.4" strokeDasharray="3,3"/>
                            <polyline points={hostilePts} fill="none" stroke="#e5696b" strokeWidth="2"/>
                            {wk.map((w,i)=>(
                                <circle key={i} cx={xAt(i)} cy={yAt(w.hostile)} r="7" fill="transparent" style={{cursor:'help'}}>
                                    <title>Week {w.week}{i===0?' (monitoring began — includes backlog)':''}: {w.hostile} hostile, {w.total} total</title>
                                </circle>
                            ))}
                        </svg>
                        <div className="font-mono text-xs mt-2" style={{color:'var(--muted)'}}>
                            <span style={{color:'#e5696b'}}>● Hostile</span>&nbsp;&nbsp;
                            <span style={{color:'#79839a'}}>— — Total volume</span>
                        </div>
                    </div>

                    {/* Severity */}
                    <div className="panel p-6">
                        <div className="font-mono text-xs uppercase tracking-widest text-muted mb-3">Severity Mix · Last 30 Days</div>
                        {sevRow('Critical', sev.critical, RATINGS.hex('critical'))}
                        {sevRow('High', sev.high, RATINGS.hex('high'))}
                        {sevRow('Moderate', sev.moderate, RATINGS.hex('moderate'))}
                        {sevRow('Low', sev.low, RATINGS.hex('low'))}
                    </div>

                </div>
            );
        }

export { OverviewTab };
